<script setup>
import { ref, computed, watch, onMounted } from 'vue'

const props = defineProps({
    node: Object,
})

const emit = defineEmits(['update:config'])

const tagId = ref(props.node?.data?.config?.tag_id ?? null)
const tags = ref([])
const loaded = ref(false)
const error = ref(null)

const search = ref('')
const isOpen = ref(false)
const highlighted = ref(0)
const inputRef = ref(null)

watch(() => props.node, (node) => {
    if (!node) return
    tagId.value = node.data?.config?.tag_id ?? null
})

onMounted(async () => {
    try {
        const response = await fetch('/bo/communications/api/v1/workflows/lead-tags')
        if (!response.ok) throw new Error('Failed to load tags')
        const result = await response.json()
        tags.value = result.data || []
    } catch (e) {
        error.value = 'Failed to load tags'
    } finally {
        loaded.value = true
    }
})

const selectedTag = computed(() => tags.value.find(t => t.id === tagId.value) ?? null)

// A selected tag that isn't in the list has been deleted since; publishing
// rejects it (VersionPublisher), so say so here rather than at publish time.
const selectedTagMissing = computed(() => loaded.value && !error.value && tagId.value !== null && selectedTag.value === null)

const filteredTags = computed(() => {
    const query = search.value.toLowerCase()
    return tags.value.filter(t => !query || t.name.toLowerCase().includes(query))
})

watch(search, () => { highlighted.value = 0 })

// tag_name is only the label the canvas node shows; the engine reads tag_id.
function selectTag(tag) {
    tagId.value = tag.id
    emit('update:config', { tag_id: tag.id, tag_name: tag.name })
    isOpen.value = false
    search.value = ''
}

function openDropdown() {
    isOpen.value = true
    search.value = ''
    highlighted.value = 0
    setTimeout(() => inputRef.value?.focus(), 50)
}

function handleBlur() {
    setTimeout(() => { isOpen.value = false }, 200)
}

function onKeydown(event) {
    if (event.key === 'ArrowDown') {
        event.preventDefault()
        highlighted.value = Math.min(highlighted.value + 1, filteredTags.value.length - 1)
    } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        highlighted.value = Math.max(highlighted.value - 1, 0)
    } else if (event.key === 'Enter') {
        event.preventDefault()
        const tag = filteredTags.value[highlighted.value]
        if (tag) selectTag(tag)
    } else if (event.key === 'Escape') {
        isOpen.value = false
    }
}
</script>

<template>
    <div class="config-panel__section">
        <label class="config-panel__label">Tag</label>
        <div class="tag-picker">
            <button
                v-if="!isOpen"
                class="tag-picker__trigger"
                type="button"
                :disabled="!loaded"
                @click="openDropdown"
            >
                <span v-if="selectedTag" class="tag-picker__selected">
                    <span class="tag-picker__swatch" :style="{ background: selectedTag.color || '#9ca3af' }"></span>
                    {{ selectedTag.name }}
                </span>
                <span v-else-if="!loaded" class="tag-picker__placeholder">Loading tags…</span>
                <span v-else class="tag-picker__placeholder">Select a tag...</span>
            </button>

            <div v-if="isOpen" class="tag-picker__dropdown">
                <input
                    ref="inputRef"
                    v-model="search"
                    type="text"
                    class="tag-picker__search"
                    placeholder="Search tags..."
                    @blur="handleBlur"
                    @keydown="onKeydown"
                />
                <div class="tag-picker__list">
                    <button
                        v-for="(tag, index) in filteredTags"
                        :key="tag.id"
                        class="tag-picker__item"
                        :class="{
                            'tag-picker__item--selected': tag.id === tagId,
                            'tag-picker__item--highlighted': index === highlighted,
                        }"
                        type="button"
                        @mousedown.prevent="selectTag(tag)"
                        @mouseenter="highlighted = index"
                    >
                        <span class="tag-picker__swatch" :style="{ background: tag.color || '#9ca3af' }"></span>
                        {{ tag.name }}
                    </button>
                    <div v-if="filteredTags.length === 0" class="tag-picker__empty">
                        {{ tags.length === 0 ? 'No tags yet' : 'No tags match your search' }}
                    </div>
                </div>
            </div>
        </div>

        <p v-if="error" class="tag-picker__warning">{{ error }}</p>
        <p v-else-if="selectedTagMissing" class="tag-picker__warning">
            The selected tag no longer exists. Pick another tag before publishing.
        </p>
        <p class="config-panel__hint">
            Contacts that already have this tag keep it and move straight on.
            Adding a tag can start workflows that trigger on Tag Assigned.
        </p>
    </div>
</template>

<style scoped>
.tag-picker {
    position: relative;
}
.tag-picker__trigger {
    display: block;
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: white;
    text-align: left;
    cursor: pointer;
    font-size: 13px;
    color: #111827;
}
.tag-picker__trigger:hover {
    border-color: #9ca3af;
}
.tag-picker__trigger:disabled {
    cursor: default;
}
.tag-picker__placeholder {
    color: #9ca3af;
}
.tag-picker__selected {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}
.tag-picker__swatch {
    flex-shrink: 0;
    width: 10px;
    height: 10px;
    border-radius: 9999px;
}
.tag-picker__dropdown {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 50;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    box-shadow: 0 10px 25px rgba(0,0,0,0.12);
}
.tag-picker__search {
    width: 100%;
    padding: 8px 12px;
    border: none;
    border-bottom: 1px solid #e5e7eb;
    font-size: 13px;
    outline: none;
    border-radius: 6px 6px 0 0;
    box-sizing: border-box;
}
.tag-picker__list {
    max-height: 280px;
    overflow-y: auto;
}
.tag-picker__item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 6px 12px;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 13px;
    text-align: left;
    color: #111827;
}
.tag-picker__item--highlighted {
    background: #f3f4f6;
}
.tag-picker__item--selected {
    background: #eff6ff;
}
.tag-picker__empty {
    padding: 12px;
    text-align: center;
    color: #9ca3af;
    font-size: 13px;
}
.tag-picker__warning {
    margin: 4px 0 0;
    font-size: 11px;
    color: #b45309;
}
</style>
